import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Formik, FormikProps } from 'formik';
import { Box, Chip, Stack, Typography, Paper, Divider } from '@mui/material';
import ButtonLoading from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import ContactlessOutlinedIcon from '@mui/icons-material/ContactlessOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { valueSafeEcryptor } from 'utils/Encryptor';
import { PATH_CONSTANT } from 'constant/PathConstant';
import {
  useAddServer,
  useServerMemberListDropdown,
  useTestServerConnection,
} from 'services/v1/TenantMaster/MasterTable/ServerTableDataService';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { DropdownCreatableMemberList } from 'pages/Tenant/AROKMS/DataInput/components/DropdownCreatableMemberList';
import { RULES_CONSTANT } from 'constant/RuleConstant';
import { Field } from 'types/api/Tenant/AROKMS/DisplayTableTypes';

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

interface FormValue {
  serverName: DropdownItem | null;
  description: string;
  host: string;
  username: string;
  password: string;
  port: string;
}

const formInitialValues: FormValue = {
  serverName: null,
  description: '',
  host: '',
  username: '',
  password: '',
  port: '',
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

function StepIndicator({ step, total, active }: { step: number; total: number; active: boolean }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1} mb={2}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: active ? '#3B4797' : '#E3EBF6',
          color: active ? '#fff' : '#9CA3AF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {step}
      </Box>
      <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
        Step {step} of {total}
      </Typography>
    </Stack>
  );
}

export default function AddServerTableData() {
  const [showPassword, setShowPassword] = useState(false);
  const formikRef = useRef<FormikProps<FormValue>>(null);
  const navigate = useNavigate();

  const { data: serverMemberListOptions } = useServerMemberListDropdown();
  const { mutate: createNewServer, isLoading: isDeploying } = useAddServer();

  const {
    mutate: testServerConnection,
    isLoading: isTesting,
    isSuccess: isServerConnected,
    isError: isServerConnectionError,
    error: serverConnectionError,
    data: serverConnectionData,
  } = useTestServerConnection();

  const handleOnTestConnection = () => {
    const payloadData = formikRef?.current?.values;
    payloadData &&
      testServerConnection({
        ...payloadData,
        serverName: payloadData?.serverName?.value as string,
        password: valueSafeEcryptor(payloadData?.password) as string,
        username: valueSafeEcryptor(payloadData?.username) as string,
      });
  };

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const handleOnSave = (data: FormValue) => {
    createNewServer(
      {
        ...data,
        serverName: data?.serverName?.value || '',
        password: valueSafeEcryptor(data?.password) as string,
        username: valueSafeEcryptor(data?.username) as string,
      },
      {
        onSuccess: () => {
          handleOnCancelInput();
        },
      },
    );
  };

  const handleOnCancelInput = () => {
    navigate(PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.SERVER);
  };

  // @ts-ignore
  const fieldItem: Field = useMemo(() => {
    if (serverMemberListOptions) {
      return {
        name: 'serverName',
        options: serverMemberListOptions.data.options,
        label: 'Server Name',
        placeholder: 'Server Name',
        colSubjectId: serverMemberListOptions.data.colSubjectId,
      };
    }
    return {} as Field;
  }, [serverMemberListOptions]);

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        <Formik innerRef={formikRef} initialValues={formInitialValues} onSubmit={handleOnSave}>
          {(formikProps: FormikProps<FormValue>) => {
            const { handleSubmit, values, handleChange, errors, touched, handleBlur } = formikProps;
            const allFieldsFilled = !Object.values(values).some((value) => !value);
            return (
              <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
                {/* Page Header */}
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
                  <Box>
                    <Typography
                      variant='body1'
                      component='h2'
                      fontWeight='bold'
                      sx={{ color: '#3B4797', fontSize: 18 }}
                    >
                      Add New Server
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                      Register a new database server by providing connection details below. You must verify the
                      connection before saving.
                    </Typography>
                  </Box>
                  <Button
                    size='small'
                    onClick={handleOnCancelInput}
                    variant='main-table-panel-border'
                    disabled={isDeploying}
                  >
                    Cancel
                  </Button>
                </Stack>

                {/* Section 1: Server Identity */}
                <Paper sx={sectionPaperStyles}>
                  <StepIndicator step={1} total={3} active={true} />
                  <SectionHeader
                    title='Server Identity'
                    description='Give this server a name and description so it can be easily identified when assigning to systems and tenants.'
                  />
                  <Divider sx={{ mb: 1 }} />

                  <DropdownCreatableMemberList
                    field={fieldItem}
                    // @ts-ignore
                    formik={formikProps}
                    ruleTypeCode={RULES_CONSTANT.BASE_SUBJECT.RULE_ATTRIBUTE_SUBJECT_BASE}
                    sx={{ width: '100%' }}
                  />

                  <FormField label='Description' required>
                    <TextField
                      fullWidth
                      size='small'
                      disabled={isDeploying}
                      multiline
                      minRows={2}
                      placeholder='e.g. Production database server for APAC region'
                      onChange={handleChange}
                      value={values.description}
                      onBlur={handleBlur}
                      name='description'
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </FormField>
                </Paper>

                {/* Section 2: Connection Details */}
                <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                  <StepIndicator step={2} total={3} active={!!values.serverName} />
                  <SectionHeader
                    title='Connection Details'
                    description='Enter the database connection parameters. These credentials will be encrypted before storage.'
                  />
                  <Divider sx={{ mb: 1 }} />

                  <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                    <Stack spacing={0.5} sx={{ flex: 3 }}>
                      <Typography sx={fieldLabelStyles}>
                        Host <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isDeploying}
                        placeholder='e.g. server.yourdomain.com'
                        onChange={handleChange}
                        value={values?.host}
                        onBlur={handleBlur}
                        name='host'
                        error={touched.host && Boolean(errors.host)}
                        helperText={touched.host && errors.host}
                      />
                    </Stack>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        Port <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isDeploying}
                        placeholder='e.g. 5432'
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.match(/^[0-9]*$/)) {
                            handleChange(e);
                          }
                        }}
                        onBlur={handleBlur}
                        value={values?.port}
                        name='port'
                        error={touched.port && Boolean(errors.port)}
                        helperText={touched.port && errors.port}
                      />
                    </Stack>
                  </Stack>

                  <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        Username <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isDeploying}
                        placeholder='Database username'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.username}
                        name='username'
                        error={touched.username && Boolean(errors.username)}
                        helperText={touched.username && errors.username}
                      />
                    </Stack>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography sx={fieldLabelStyles}>
                        Password <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isDeploying}
                        type={showPassword ? 'text' : 'password'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Database password'
                        value={values?.password}
                        name='password'
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton onClick={handlePasswordToggle} size='small'>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                {/* Section 3: Connection Test */}
                <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                  <StepIndicator step={3} total={3} active={allFieldsFilled} />
                  <SectionHeader
                    title='Verify Connection'
                    description='Test the database connection to ensure the server is reachable and the credentials are correct. A successful test is required before saving.'
                  />
                  <Divider sx={{ mb: 2 }} />

                  <Stack direction='row' alignItems='center' spacing={2}>
                    <ButtonLoading
                      disabled={!allFieldsFilled}
                      variant='main-table-panel'
                      loading={isTesting}
                      startIcon={<ContactlessOutlinedIcon />}
                      onClick={handleOnTestConnection}
                    >
                      Test Connection
                    </ButtonLoading>

                    {serverConnectionData !== undefined && isServerConnected && (
                      <Chip
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                        label='Connected'
                        color='success'
                        sx={{ bgcolor: '#42BB93', fontWeight: 500 }}
                      />
                    )}

                    {serverConnectionData === undefined && isServerConnectionError && (
                      <Chip
                        icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
                        // @ts-ignore
                        label={serverConnectionError?.response?.data?.message || 'Connection failed'}
                        color='error'
                        variant='outlined'
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                  </Stack>

                  {serverConnectionData === undefined && !isServerConnectionError && !isTesting && (
                    <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 1.5 }}>
                      Fill in all connection details above, then click "Test Connection" to verify.
                    </Typography>
                  )}
                </Paper>

                {/* Action Buttons */}
                <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
                  <Button disabled={isDeploying} onClick={handleOnCancelInput} variant='main-table-panel-border'>
                    Cancel
                  </Button>
                  <ButtonLoading
                    disabled={!allFieldsFilled || !isServerConnected}
                    variant='main-table-panel'
                    loading={isDeploying}
                    onClick={() => handleSubmit()}
                  >
                    Save Server
                  </ButtonLoading>
                </Stack>
              </Box>
            );
          }}
        </Formik>
      </Box>
    </TenantHomeDashboardNavigationBar>
  );
}
